/**
 * UNIVERSAL LABEL BUILDER v1.0
 * One front-end for six label shape products and eleven label material products.
 * Apippa Custom Price Calculator: https://api.custompricecalculator.com/api/v1/variant
 * Shopify: POST /cart/add.js with quantity=1; actual label count is a line-item property.
 * This script does not create or assign the Apippa calculator in the app dashboard.
 */
(() => {
  'use strict';

  const MATERIAL_RATES = Object.freeze({
    'Vinyl Labels': 0.084,
    'Matte Labels': 0.086,
    'Holographic Labels': 0.123,
    'Clear Labels': 0.086,
    'Heavy duty Labels': 0.112,
    'Reflective Labels': 0.232,
    'Prismatic Labels': 0.1343,
    'Glitter Labels': 0.1343,
    'Gold Mirror Labels': 0.1972,
    'Gold Brushed Labels': 0.1972,
    'Gold Glitter Labels': 0.1972
  });
  const SHAPES = ['Diecut', 'Circle', 'Square', 'Rectangle', 'Rounded', 'Oval'];
  const QUANTITY_PRESETS = [50, 100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000];
  // Identical to Print Custom Labels calculation: max discount tier is 5000.
  const DISCOUNT_BREAKPOINTS = [10, 50, 100, 200, 300, 500, 800, 1000, 1500, 2000, 5000];
  const SUPPORTED_FILE = /\.(ai|eps|jpe?g|png|pdf|tif|tiff|svg)$/i;
  const REQUIRED_APP_KEYS = ['Width', 'Height', 'Quantity', 'Discount', 'Material', 'Cutline'];

  const SHAPE_ALIASES = [
    ['Diecut', /\b(die[\s-]?cut|diecut|contour[\s-]?cut)\b/],
    ['Circle', /\bcircle\b/],
    ['Square', /\bsquare\b/],
    ['Rectangle', /\brectangle\b/],
    ['Rounded', /\b(round(ed)?[\s-]?(rectangle|corner)?|rounded)\b/],
    ['Oval', /\boval\b/]
  ];
  // Most specific names first (so "Gold Glitter" is NOT confused with plain "Glitter").
  const MATERIAL_ALIASES = [
    ['Gold Glitter Labels', /\b(gold[\s-]*glitter|glitter[\s-]*gold)\b/],
    ['Gold Brushed Labels', /\b(gold[\s-]*brushed|brushed[\s-]*gold)\b/],
    ['Gold Mirror Labels', /\b(gold[\s-]*mirror|mirror[\s-]*gold)\b/],
    ['Heavy duty Labels', /\bheavy[\s-]*duty\b/],
    ['Holographic Labels', /\bholographic\b/],
    ['Reflective Labels', /\breflective\b/],
    ['Prismatic Labels', /\bprismatic\b/],
    ['Glitter Labels', /\bglitter\b/],
    ['Clear Labels', /\b(clear|transparent)\b/],
    ['Matte Labels', /\bmatte\b/],
    ['Vinyl Labels', /\bvinyl\b/]
  ];

  function findMatch(text, aliases) {
    return aliases.find(([name, regexp]) => regexp.test(text))?.[0] || null;
  }
  function normalize(name) {return String(name || '').replace(/[_/]+/g, ' ').replace(/-/g, ' ').toLowerCase();}
  function resolveCatalogProduct(config) {
    const terms = normalize([config.title, config.handle].join(' '));
    const tags = config.tags.map(normalize);
    const taggedMode = tags.find(tag => /^label[\s-]?mode[:=]/.test(tag));
    const parsedTagMode = taggedMode?.split(/[:=]/)[1]?.trim();
    const tagShape = tags.find(tag => /^shape[:=]/.test(tag));
    const tagMaterial = tags.find(tag => /^material[:=]/.test(tag));
    const tagShapeValue = tagShape && findMatch(normalize(tagShape.split(/[:=]/).slice(1).join(':')), SHAPE_ALIASES);
    const tagMaterialValue = tagMaterial && findMatch(normalize(tagMaterial.split(/[:=]/).slice(1).join(':')), MATERIAL_ALIASES);

    const knownShape = tagShapeValue || findMatch(terms, SHAPE_ALIASES);
    const knownMaterial = tagMaterialValue || findMatch(terms, MATERIAL_ALIASES);
    let mode = config.mode === 'auto' ? (parsedTagMode === 'shape' || parsedTagMode === 'material' ? parsedTagMode : '') : config.mode;
    if (!mode) mode = knownShape ? 'shape' : knownMaterial ? 'material' : '';
    const explicitlyMaterial = config.material !== 'auto' ? config.material : null;
    const explicitlyShape = config.shape !== 'auto' ? config.shape : null;
    if (!mode && explicitlyShape && !explicitlyMaterial) mode = 'shape';
    if (!mode && explicitlyMaterial && !explicitlyShape) mode = 'material';
    if (!mode && !config.strict) mode = 'material';
    if (!['shape', 'material'].includes(mode)) throw new Error('This product has no label builder configuration. Add a Product configuration block and choose Shape or Material mode.');

    // Missing catalog values are blocked in strict mode: never silently charge Vinyl for an unknown finish.
    if (config.strict && mode === 'shape' && !explicitlyShape && !knownShape) throw new Error('Shape is not recognized. Set the shape explicitly in its Product configuration block.');
    if (config.strict && mode === 'material' && !explicitlyMaterial && !knownMaterial) throw new Error('Material is not recognized. Set the material explicitly in its Product configuration block.');
    // Shape products ALWAYS price as Vinyl. Material products ALWAYS have Diecut cutline.
    const shape = mode === 'shape' ? (explicitlyShape || knownShape || 'Diecut') : 'Diecut';
    const material = mode === 'material' ? (explicitlyMaterial || knownMaterial || 'Vinyl Labels') : 'Vinyl Labels';
    if (!SHAPES.includes(shape)) throw new Error(`Unsupported shape: ${shape}.`);
    if (!Object.prototype.hasOwnProperty.call(MATERIAL_RATES, material)) throw new Error(`Unknown material: ${material}.`);
    return {mode, shape, material, rate: MATERIAL_RATES[material]};
  }
  function discountTier(quantity) {
    let selected = 10;
    for (const threshold of DISCOUNT_BREAKPOINTS) {
      if (quantity >= threshold) selected = threshold;
      else break;
    }
    return selected;
  }
  function dimensionTier(width, height) {
    return Math.min(Math.max(Math.round(width), Math.round(height)), 8);
  }
  function findProperty(form, key) {
    return [...form.querySelectorAll('[name]')].find(field => field.name === `properties[${key}]`) || null;
  }
  function updateFormProperty(form, key, value) {
    const field = findProperty(form, key);
    if (!field) return false;
    if (field.type === 'checkbox') {
      field.checked = Boolean(value);
    } else if (field.tagName === 'SELECT') {
      const option = [...field.options].find(opt => opt.value === String(value) || opt.text.trim() === String(value));
      if (!option) return false;
      field.value = option.value;
    } else if (field.type !== 'file') {
      field.value = String(value == null ? '' : value);
    }
    return true;
  }
  function nearestPreset(q) {
    return QUANTITY_PRESETS.reduce((closest, n) => Math.abs(n-q) < Math.abs(closest-q) ? n : closest, QUANTITY_PRESETS[0]);
  }
  function sleep(ms) {return new Promise(resolve => setTimeout(resolve, ms));}

  function setup(root) {
    if (root.dataset.ulbInitialized === 'true') return;
    root.dataset.ulbInitialized = 'true';
    const $ = selector => root.querySelector(selector);
    const $$ = selector => [...root.querySelectorAll(selector)];
    const el = {
      sizes: $$('[data-usb-size]'), hint: $('[data-usb-size-hint]'), sizeBox: $('[data-usb-custom-size]'),
      width: $('[data-usb-width]'), height: $('[data-usb-height]'), sizeError: $('[data-usb-size-error]'),
      minus: $('[data-usb-minus]'), plus: $('[data-usb-plus]'), total: $('[data-usb-total]'), unit: $('[data-usb-unit]'),
      range: $('[data-usb-range]'), presetBox: $('[data-usb-presets]'), tiers: $$('[data-usb-tier]'),
      exactToggle: $('[data-usb-exact-toggle]'), exactArea: $('[data-usb-exact-area]'), exact: $('[data-usb-exact]'),
      presetReturn: $('[data-usb-return-presets]'), quantityError: $('[data-usb-qty-error]'),
      continue: $('[data-usb-continue]'), priceStatus: $('[data-usb-status]'),
      firstStep: $('[data-usb-step="configure"]'), artworkStep: $('[data-usb-step="artwork"]'), back: $('[data-usb-back]'),
      recapSize: $('[data-usb-recap-size]'), recapQty: $('[data-usb-recap-qty]'), recapTotal: $('[data-usb-recap-total]'),
      buttonPrice: $('[data-usb-button-price]'), methodOptions: $$('[data-usb-method]'),
      uploadPane: $('[data-usb-upload-pane]'), laterPane: $('[data-usb-later-pane]'),
      drop: $('[data-usb-dropzone]'), choose: $('[data-usb-choose]'),
      file: $('[data-usb-file-input]'), filename: $('[data-usb-file-name]'), proof: $('[data-usb-proof]'),
      instructions: $('[data-usb-instructions]'), artError: $('[data-usb-art-error]'),
      artStatus: $('[data-usb-art-status]'), add: $('[data-usb-add]'), overlay: $('[data-usb-processing]'),
      overlayText: $('[data-usb-processing-text]'), overlayProgress: $('[data-usb-processing-progress]')
    };
    const config = {
      productId: String(root.dataset.productId || '').trim(),
      actualProductId: String(root.dataset.actualProductId || '').trim(),
      calculatorId: String(root.dataset.calculatorId || '').trim(),
      title: root.dataset.productTitle || '',
      handle: root.dataset.productHandle || '',
      mode: root.dataset.productMode || 'auto',
      shape: root.dataset.shape || 'auto',
      material: root.dataset.material || 'auto',
      tags: [],
      strict: root.dataset.strictMatching !== 'false',
      defaultSize: Number(root.dataset.defaultSize || 3),
      defaultQty: Number(root.dataset.defaultQty || 200),
      minPrice: Number(root.dataset.minPrice || 15),
      maxSize: Number(root.dataset.maxSize || 52),
      maxQty: Number(root.dataset.maxQuantity || 50000),
      maxFileBytes: Number(root.dataset.maxUploadMb || 20)*1024*1024,
      cartBehavior: root.dataset.autoCart || 'drawer',
      currency: root.dataset.moneyCurrency || 'USD',
      cartRoot: root.dataset.cartRoot || '/'
    };
    try {config.tags = JSON.parse($('[data-usb-product-tags]')?.textContent || '[]');}
    catch (_) {config.tags = [];}
    // Section-wide product override is intended only for a dedicated product template.
    const state = {
      catalog: null, error: '', discounts: null, discountReady: false,
      size: String(config.defaultSize), width: config.defaultSize, height: config.defaultSize,
      quantity: config.defaultQty, exact: false, method: 'upload', file: null,
      price: null, multiplier: null, appForm: null, busy: false
    };
    let formCheckTimer = null;
    let updateFormFrame = 0;
    try {
      if (!/^\d+$/.test(config.productId)) throw new Error('A valid Shopify product ID is required. Add this section to a Shopify product template.');
      if (config.actualProductId && config.productId !== config.actualProductId) throw new Error('The Product ID override does not match the current Shopify product. Remove the override or use the matching product ID.');
      if (!Number.isFinite(config.minPrice) || config.minPrice < 0) throw new Error('Minimum price must be zero or higher.');
      if (!Number.isFinite(config.maxSize) || config.maxSize < 1) throw new Error('Invalid maximum custom size setting.');
      state.catalog = resolveCatalogProduct(config);
    } catch (e) {state.error = e.message;}

    function message(target, text) {
      if (!target) return;
      target.textContent = text || '';
      target.hidden = !text;
    }
    function money(number, extraDecimals = false) {
      if (!Number.isFinite(number)) return '—';
      try {
        return new Intl.NumberFormat('en-US', {style:'currency', currency:config.currency,
          minimumFractionDigits:extraDecimals ? 4 : 2, maximumFractionDigits:extraDecimals ? 4 : 2}).format(number);
      } catch (_) {return '$' + number.toFixed(extraDecimals ? 4 : 2);}
    }
    function sizesValid() {
      return Number.isFinite(state.width) && Number.isFinite(state.height) && state.width >= 1 && state.height >= 1 && state.width <= config.maxSize && state.height <= config.maxSize;
    }
    function quantityValid() {
      return Number.isInteger(state.quantity) && state.quantity >= 1 && state.quantity <= config.maxQty;
    }
    function effectiveCalculatorId() {
      return config.calculatorId || String(state.appForm?.dataset.calcid || '').trim();
    }
    function findAppForm() {
      const forms = [...document.querySelectorAll('#calculator[data-calcid], form#calculator')];
      return forms.find(f => {
        const productId = f.querySelector('input[name="product-id"]')?.value || '';
        if (productId && productId !== config.productId) return false;
        return !config.calculatorId || !f.dataset.calcid || config.calculatorId === f.dataset.calcid;
      }) || null;
    }
    function propertyPayload() {
      const props = {};
      const form = state.appForm;
      if (form) {
        for (const input of form.querySelectorAll('[name^="properties["]')) {
          if (input.type === 'file' || !input.name.endsWith(']')) continue;
          const key = input.name.slice(11,-1);
          if (input.type === 'checkbox') props[key] = input.checked ? 'Checked' : 'Unchecked';
          else if ((input.type === 'radio' && input.checked) || input.type !== 'radio') props[key] = input.value;
        }
      }
      Object.assign(props, {
        'Cutline': state.catalog.shape,
        'Material': state.catalog.material,
        'Width': String(state.width),
        'Height': String(state.height),
        'Quantity': String(state.quantity),
        'Discount': String(state.multiplier),
        'No Image': state.method === 'later' ? 'Request an image from the client.' : '',
        'Instructions': el.instructions.value,
        'Design Proof Before Shipping ': state.method === 'upload' && el.proof.checked ? 'Checked' : 'Unchecked'
      });
      // Keep exactly the app's property names used in the original Print Custom Labels script.
      return props;
    }
    function syncForm() {
      if (!state.catalog || !sizesValid() || !quantityValid() || state.multiplier == null) return;
      const form = state.appForm;
      if (!form || !form.isConnected) return;
      const props = propertyPayload();
      for (const key of REQUIRED_APP_KEYS) {
        if (findProperty(form,key)) updateFormProperty(form,key,props[key]);
      }
      updateFormProperty(form,'No Image',state.method === 'later' ? 'Request an image from the client.' : '');
      updateFormProperty(form,'Instructions',el.instructions.value);
      updateFormProperty(form,'Design Proof Before Shipping ',state.method === 'upload' && el.proof.checked);
      // PriceUpdation is an optional calculator UI sync, never our primary pricing source.
      if (typeof window.PriceUpdation === 'function') {
        try {window.PriceUpdation();} catch (error) {console.warn('[Universal Label Builder] PriceUpdation:', error);}
      }
    }
    function scheduleSync() {
      if (updateFormFrame) cancelAnimationFrame(updateFormFrame);
      updateFormFrame = requestAnimationFrame(() => {updateFormFrame = 0; syncForm();});
    }
    function computePrice() {
      if (!state.catalog || !state.discountReady || !sizesValid() || !quantityValid()) return null;
      const table = state.discounts?.[state.catalog.material];
      if (!table || typeof table !== 'object') throw new Error(`Discount JSON has no entry for ${state.catalog.material}.`);
      const sizeKey = dimensionTier(state.width,state.height);
      const qtyKey = discountTier(state.quantity);
      const discountPercentage = Number(table?.[sizeKey]?.[qtyKey] ?? 0);
      if (!Number.isFinite(discountPercentage) || discountPercentage < 0 || discountPercentage >= 100) throw new Error('Invalid discount value in discount JSON.');
      const multiplier = (100-discountPercentage)/100;
      const base = state.width*state.height*state.quantity*state.catalog.rate;
      return {price: Math.max(config.minPrice,base*multiplier),multiplier,discountPercentage};
    }
    function refreshUI() {
      let priceError = '';
      try {
        const result = computePrice();
        state.price = result?.price ?? null;
        state.multiplier = result?.multiplier ?? null;
      } catch (e) {priceError = e.message; state.price = null; state.multiplier = null;}
      el.total.textContent = money(state.price);
      el.unit.textContent = state.price == null || !quantityValid() ? 'Calculating price…' : `${money(state.price / state.quantity, state.price/state.quantity < 0.01)} / Label`;
      const status = state.error || priceError || (!state.discountReady ? 'Loading label discount pricing…' : '') || (!effectiveCalculatorId() ? 'Set the Apippa calculator ID in section settings or assign the calculator app to this product.' : '');
      message(el.priceStatus,status);
      el.continue.disabled = !!status || !sizesValid() || !quantityValid() || state.price == null;
      el.recapSize.textContent = `${state.width || '—'}” × ${state.height || '—'}”`;
      el.recapQty.textContent = String(Number.isFinite(state.quantity) ? state.quantity : '—');
      el.recapTotal.textContent = money(state.price);
      el.buttonPrice.textContent = money(state.price);
      if (!status) scheduleSync();
    }
    function updatePresetUI() {
      let index = QUANTITY_PRESETS.indexOf(state.quantity);
      if (index < 0) index = QUANTITY_PRESETS.indexOf(nearestPreset(Number.isFinite(state.quantity) ? state.quantity : config.defaultQty));
      el.range.value = String(index);
      el.range.disabled = state.exact;
      el.range.style.setProperty('--usb-progress', `${100*index/(QUANTITY_PRESETS.length-1)}%`);
      el.range.setAttribute('aria-valuetext', `${Number.isFinite(state.quantity) ? state.quantity : 0} labels`);
      el.presetBox.classList.toggle('is-disabled', state.exact);
      el.tiers.forEach(btn => {
        const active = !state.exact && Number(btn.dataset.usbTier) === state.quantity;
        btn.classList.toggle('is-current',active);
        btn.setAttribute('aria-current',active ? 'true' : 'false');
      });
      el.exactArea.hidden = !state.exact;
      el.exactToggle.setAttribute('aria-expanded',String(state.exact));
      el.exactToggle.textContent = state.exact ? 'Custom quantity selected' : 'Enter exact amount.';
      if (state.exact) el.exact.value = Number.isFinite(state.quantity) ? String(state.quantity) : '';
    }
    function setQuantity(quantity, exact) {
      state.quantity = quantity;
      state.exact = exact;
      message(el.quantityError,'');
      updatePresetUI();
      refreshUI();
    }
    function setSize(choice) {
      state.size = choice;
      const custom = choice === 'custom';
      el.sizeBox.hidden = !custom;
      el.sizes.forEach(btn => {
        const active = btn.dataset.usbSize === choice;
        btn.classList.toggle('is-active',active);
        btn.setAttribute('aria-pressed',String(active));
      });
      const hints = {
        '2':'Perfect for smaller labels and branding.',
        '3':'About the width of a credit card.',
        '4':'A versatile size for packaging and promotion.',
        '5':'Ideal for standout designs.'
      };
      el.hint.textContent = custom ? `Enter custom dimensions between 1 and ${config.maxSize} inches.` : hints[choice];
      if (custom) {
        // Do not keep previous dimensions after switching from a preset.
        el.width.value = ''; el.height.value = '';
        state.width = NaN; state.height = NaN;
        requestAnimationFrame(() => el.width.focus());
      } else {
        state.width = Number(choice); state.height = Number(choice);
      }
      message(el.sizeError,'');
      refreshUI();
    }
    function setMethod(method) {
      state.method = method;
      el.methodOptions.forEach(btn => {
        const active = btn.dataset.usbMethod === method;
        btn.classList.toggle('is-active',active);
        btn.setAttribute('aria-pressed',String(active));
      });
      el.uploadPane.hidden = method !== 'upload';
      el.laterPane.hidden = method !== 'later';
      message(el.artError,'');
      scheduleSync();
    }
    function changeStep(step) {
      el.firstStep.hidden = step !== 'configure';
      el.artworkStep.hidden = step !== 'artwork';
      const scrollTarget = root.querySelector('.usb-card');
      if (scrollTarget && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const top = scrollTarget.getBoundingClientRect().top + window.scrollY - 45;
        window.scrollTo({top,behavior:'smooth'});
      }
    }
    function receiveFile(file) {
      if (!file) return;
      if (!SUPPORTED_FILE.test(file.name)) {state.file=null;el.filename.hidden=true;el.file.value='';message(el.artError,'Unsupported file. Please choose AI, EPS, JPG, PNG, PDF, TIFF or SVG.');return;}
      if (file.size > config.maxFileBytes) {state.file=null;el.filename.hidden=true;el.file.value='';message(el.artError,`Artwork exceeds the ${Math.round(config.maxFileBytes/1048576)} MB upload limit.`);return;}
      state.file = file;
      el.filename.textContent = `✓ ${file.name}`;
      el.filename.hidden = false;
      message(el.artError,'');
      // Mirror to the app's file input (if available), preserving the original app's behavior.
      const nativeInput = state.appForm?.querySelector('input[type="file"]');
      if (nativeInput && nativeInput !== el.file) {
        try {
          const transfer = new DataTransfer();transfer.items.add(file);
          nativeInput.files = transfer.files;
          nativeInput.dispatchEvent(new Event('change',{bubbles:true}));
        } catch (error) {console.warn('[Universal Label Builder] Could not mirror native file input:',error);}
      }
    }
    function showProcessing(text,percentage) {
      el.overlay.hidden = false;
      el.overlayText.textContent = text;
      el.overlayProgress.style.width = `${percentage}%`;
    }
    function closeProcessing() {el.overlay.hidden = true;el.overlayProgress.style.width='0%';}
    function rootPath(path) {return config.cartRoot.replace(/\/$/,'/') + path.replace(/^\//,'');}
    async function cartDrawerRefresh() {
      if (config.cartBehavior === 'cart') {window.location.assign(rootPath('cart'));return;}
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        try {
          const [cartRes, sectionRes] = await Promise.all([
            fetch(rootPath('cart.js'),{credentials:'same-origin'}),
            fetch(`${rootPath('cart')}?section_id=mini-cart&refresh=${Date.now()}`,{credentials:'same-origin'})
          ]);
          if (cartRes.ok && sectionRes.ok) {
            const cart = await cartRes.json();
            const markup = await sectionRes.text();
            const html = new DOMParser().parseFromString(markup,'text/html').querySelector('cart-drawer')?.outerHTML;
            if (html) {
              drawer.dispatchEvent(new CustomEvent('cart:refresh',{
                bubbles:true,
                detail:{cart:{...cart,sections:{'mini-cart':html}},openMiniCart:true}
              }));
              const count = document.querySelector('cart-count');
              if (count) count.textContent = String(cart.item_count);
              return;
            }
          }
        } catch (e) {console.warn('[Universal Label Builder] Cart drawer:',e);}
      }
      window.location.assign(rootPath('cart'));
    }
    async function addToCart() {
      if (state.busy) return;
      if (!sizesValid() || !quantityValid() || state.price == null) {changeStep('configure');return;}
      if (!effectiveCalculatorId()) {message(el.artStatus,'An Apippa calculator ID is required.');return;}
      if (state.method === 'upload' && !state.file) {message(el.artError,'Please upload artwork or choose Email artwork later.');return;}
      state.busy = true;el.add.disabled = true;
      message(el.artError,'');message(el.artStatus,'');
      let added = false;
      try {
        showProcessing('Syncing your size, quantity and pricing…',15);
        const properties = propertyPayload();
        syncForm();
        if (!Number.isFinite(Number(properties.Discount)) || Number(properties.Discount) <= 0) throw new Error('Invalid discount multiplier.');
        const request = {calculatorId:effectiveCalculatorId(),productId:config.productId,properties};
        showProcessing('Creating your custom-priced label variant…',40);
        const response = await fetch('https://api.custompricecalculator.com/api/v1/variant',{
          method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},
          body:JSON.stringify(request)
        });
        if (!response.ok) throw new Error(`Apippa variant creation failed (HTTP ${response.status}). Check calculator/product ID assignment.`);
        const data = await response.json();
        const variantId = typeof data === 'string' || typeof data === 'number' ? data : data?.variantId || data?.id;
        if (!/^\d+$/.test(String(variantId || ''))) throw new Error('Apippa did not return a valid Shopify variant ID.');
        const body = new FormData();
        body.append('id',String(variantId));
        body.append('quantity','1'); // Full job price is the variant price — not per-label price.
        Object.entries(properties).forEach(([key,value]) => body.append(`properties[${key}]`,String(value ?? '')));
        if (state.method === 'upload') body.append('properties[Upload Image]',state.file,state.file.name);
        showProcessing('Adding your labels to Shopify cart…',72);
        const cartResponse = await fetch(rootPath('cart/add.js'),{
          method:'POST',body,credentials:'same-origin'
        });
        const item = await cartResponse.json();
        if (!cartResponse.ok) throw new Error(item?.description || item?.message || 'Shopify could not add this order.');
        added = true;
        try {
          localStorage.setItem(`patched-cart-item-${item.id}`,JSON.stringify({
            id:item.id,title:item.product_title,image:item.image,price:item.price
          }));
        } catch (_) { /* Optional theme cache. */ }
        showProcessing('Updating your shopping cart…',90);
        await cartDrawerRefresh();
        showProcessing('Added to cart!',100);
        await sleep(250);
        closeProcessing();
        el.add.textContent = 'Added to cart';
        // Reset to product defaults after the success state so the same section can handle another order.
      } catch (error) {
        closeProcessing();
        message(el.artStatus,added ? `Order was added, but your cart could not refresh: ${error.message}` : error.message);
        console.error('[Universal Label Builder] Add to cart:',error);
      } finally {
        state.busy=false;
        el.add.disabled=added;
        if (!added) {
          el.add.innerHTML = 'Add to cart — <span data-usb-button-price></span>';
          el.buttonPrice = $('[data-usb-button-price]') || el.buttonPrice;
          if (el.buttonPrice.isConnected) el.buttonPrice.textContent = money(state.price);
        } else {
          window.setTimeout(() => {
            if (!root.isConnected) return;
            state.file=null;el.file.value='';el.filename.hidden=true;
            const native=state.appForm?.querySelector('input[type="file"]');if(native) native.value='';
            el.proof.checked=false;el.instructions.value='';
            setSize(String(config.defaultSize));
            setQuantity(QUANTITY_PRESETS.includes(config.defaultQty)?config.defaultQty:200,false);
            setMethod('upload');changeStep('configure');
            el.add.innerHTML = 'Add to cart — <span data-usb-button-price></span>';
            el.buttonPrice = $('[data-usb-button-price]');
            el.buttonPrice.textContent=money(state.price);
            el.add.disabled=false;
          },1200);
        }
      }
    }
    async function loadDiscounts() {
      if (state.error) {refreshUI();return;}
      try {
        const url = root.dataset.discountsUrl;
        if (!url) throw new Error('Missing print-custom-labels-discounts.json URL.');
        const response = await fetch(url,{credentials:'same-origin'});
        if (!response.ok) throw new Error(`Unable to load discount JSON (HTTP ${response.status}).`);
        const data = await response.json();
        if (!data || typeof data !== 'object' || !data[state.catalog.material]) throw new Error(`Discount JSON is missing ${state.catalog.material}.`);
        state.discounts = data;
        state.discountReady = true;
        refreshUI();
      } catch (error) {
        state.error = error.message;
        state.discountReady = false;
        refreshUI();
      }
    }
    function checkAppForm() {
      const found = findAppForm();
      if (found !== state.appForm) {
        state.appForm = found;
        if (state.appForm && config.calculatorId && String(state.appForm.dataset.calcid || '') !== config.calculatorId) {
          console.info('[Universal Label Builder] Using the section calculator ID override rather than the visible app form.');
          state.appForm = null;
        }
        refreshUI();
      }
    }
    // User events.
    el.sizes.forEach(btn => btn.addEventListener('click',() => setSize(btn.dataset.usbSize)));
    const dimensionChanged = () => {
      state.width = el.width.value.trim() ? Number(el.width.value) : NaN;
      state.height = el.height.value.trim() ? Number(el.height.value) : NaN;
      message(el.sizeError,'');refreshUI();
    };
    el.width.max = String(config.maxSize);el.height.max=String(config.maxSize);
    el.width.addEventListener('input',dimensionChanged);
    el.height.addEventListener('input',dimensionChanged);
    el.range.addEventListener('input',() => setQuantity(QUANTITY_PRESETS[Number(el.range.value)],false));
    el.tiers.forEach(btn => btn.addEventListener('click',() => {if (!state.exact) setQuantity(Number(btn.dataset.usbTier),false);}));
    el.exactToggle.addEventListener('click',() => {setQuantity(state.quantity,true);requestAnimationFrame(()=>el.exact.focus());});
    el.presetReturn.addEventListener('click',() => setQuantity(nearestPreset(Number.isFinite(state.quantity) ? state.quantity : config.defaultQty),false));
    el.exact.max = String(config.maxQty);
    el.exact.addEventListener('input',() => {
      state.quantity = el.exact.value.trim() ? Number(el.exact.value) : NaN;
      message(el.quantityError,'');
      el.range.setAttribute('aria-valuetext',`${Number.isFinite(state.quantity)?state.quantity:0} labels`);
      refreshUI();
    });
    el.plus.addEventListener('click',() => setQuantity(Math.min(config.maxQty,(quantityValid()?state.quantity:config.defaultQty)+1),true));
    el.minus.addEventListener('click',() => setQuantity(Math.max(1,(quantityValid()?state.quantity:config.defaultQty)-1),true));
    el.continue.addEventListener('click',() => {
      message(el.sizeError,sizesValid() ? '' : `Width and height must both be between 1 and ${config.maxSize} inches.`);
      message(el.quantityError,quantityValid() ? '' : `Please enter a whole quantity from 1 to ${config.maxQty}.`);
      if (el.continue.disabled || !sizesValid() || !quantityValid()) return;
      syncForm();changeStep('artwork');
    });
    el.back.addEventListener('click',() => changeStep('configure'));
    el.methodOptions.forEach(btn => btn.addEventListener('click',() => setMethod(btn.dataset.usbMethod)));
    el.choose.addEventListener('click',event => {event.stopPropagation();el.file.click();});
    el.drop.addEventListener('click',event=>{if (!event.target.closest('button')) el.file.click();});
    el.drop.addEventListener('dragover',event=>{event.preventDefault();el.drop.classList.add('is-dragover');});
    el.drop.addEventListener('dragleave',()=>el.drop.classList.remove('is-dragover'));
    el.drop.addEventListener('drop',event=>{
      event.preventDefault();el.drop.classList.remove('is-dragover');receiveFile(event.dataTransfer?.files?.[0]);
    });
    el.file.addEventListener('change',()=>receiveFile(el.file.files?.[0]));
    el.proof.addEventListener('change',scheduleSync);
    el.instructions.addEventListener('input',scheduleSync);
    el.add.addEventListener('click',addToCart);

    // An app may be injected after page load. No mutation observer cycles needed.
    checkAppForm();
    let checks = 0;
    formCheckTimer = window.setInterval(() => {
      if (!root.isConnected || checks++ > 50) {window.clearInterval(formCheckTimer);return;}
      checkAppForm();
    },600);
    setSize(String(config.defaultSize));
    setQuantity(QUANTITY_PRESETS.includes(config.defaultQty) ? config.defaultQty : 200,false);
    setMethod('upload');
    loadDiscounts();
  }

  function boot() {document.querySelectorAll('[data-ulb-builder]').forEach(setup);}
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',boot);
  else boot();
  document.addEventListener('shopify:section:load',boot);
})();
